import { ArrowRight, ArrowDown, Check, TrendingUp, Shield, BarChart3, ZoomIn } from 'lucide-react';
import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ImageModal, PayoutCarousel } from '../components/shared';
import { useLanguage } from '../context/LanguageContext';

const QuantitativePage = () => {
  const { t } = useLanguage();
  const [modalImage, setModalImage] = useState({ isOpen: false, src: '', alt: '' });
  
  const openModal = (src, alt) => {
    setModalImage({ isOpen: true, src, alt });
  };
  
  const closeModal = () => {
    setModalImage({ isOpen: false, src: '', alt: '' });
  };
  
  return (
    <div className="min-h-screen bg-gray-950" data-testid="quantitative-page">
      <Navbar />
      
      {/* Image Modal */}
      <ImageModal 
        isOpen={modalImage.isOpen} 
        onClose={closeModal} 
        imageSrc={modalImage.src} 
        imageAlt={modalImage.alt} 
      />
      
      {/* Hero - Como estaba antes, con shadow abajo */}
      <section className="relative h-[50vh] md:h-screen pt-16 md:pt-24" data-testid="quant-hero">
        <img 
          src="https://static.wixstatic.com/media/7a40f4_ee889582a7c84970981afa258d79a0b7~mv2.jpg"
          alt="Accretion Axiom Quantitative"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradiente sutil solo en el borde inferior para la transición */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent"></div>
        
        {/* Flecha para scroll - solo desktop, azul fosforescente */}
        <button 
          className="hidden md:block absolute bottom-6 left-1/2 -translate-x-1/2 transition-all animate-bounce cursor-pointer"
          style={{ color: '#00f5ff' }}
          onClick={() => {
            window.scrollTo({ top: window.innerHeight * 0.815, behavior: 'smooth' });
          }}
        >
          <ArrowDown size={52} strokeWidth={1.5} />
        </button>
      </section>

      {/* The Axiom Strategy - Con iconos azules */}
      <section id="strategy" className="py-16 bg-gray-950 scroll-mt-48" data-testid="quant-strategy">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-20">
            <p className="text-sm tracking-[0.3em] mb-4 text-cyan-400">{t('quantitative.dao')}</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white leading-tight">
              {t('quantitative.strategyTitle')}
            </h2>
            <p className="text-lg text-gray-400 max-w-4xl mx-auto leading-relaxed italic">
              {t('quantitative.strategyDesc')}
            </p>
          </div>

          {/* Stats Grid con iconos azules */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 border border-gray-800 bg-gray-900/50" data-testid="stat-apy">
              <TrendingUp className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
              <p className="text-sm tracking-[0.2em] text-gray-500 mb-2">{t('quantitative.targetApy')}</p>
              <h3 className="text-3xl font-bold text-white mb-2">20-25%</h3>
              <p className="text-gray-400 text-sm">{t('quantitative.projectedReturn')}</p>
            </div>
            <div className="text-center p-8 border border-gray-800 bg-gray-900/50" data-testid="stat-terms">
              <Shield className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
              <p className="text-sm tracking-[0.2em] text-gray-500 mb-2">{t('quantitative.terms')}</p>
              <h3 className="text-3xl font-bold text-white mb-2">12-Month</h3>
              <p className="text-gray-400 text-sm">{t('quantitative.lockUp')} • €2000 {t('quantitative.minimum')}</p>
            </div>
            <div className="text-center p-8 border border-gray-800 bg-gray-900/50" data-testid="stat-onboarding">
              <BarChart3 className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
              <p className="text-sm tracking-[0.2em] text-gray-500 mb-2">{t('quantitative.onboarding')}</p>
              <h3 className="text-3xl font-bold text-white mb-2">{t('quantitative.flexible')}</h3>
              <p className="text-gray-400 text-sm">{t('quantitative.paymentMethods')}</p>
            </div>
          </div>

          <div className="text-center mt-16 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href="#results" 
              className="border border-cyan-500 text-cyan-400 px-8 py-4 text-lg font-semibold hover:bg-cyan-500/10 transition-all inline-flex items-center"
              data-testid="verified-results-btn"
            >
              {t('quantitative.verifiedResults')}
              <Check className="ml-2" size={20} />
            </a>
            <a 
              href="#invest" 
              className="bg-cyan-500 text-gray-950 px-10 py-4 text-lg font-semibold hover:bg-cyan-400 transition-all inline-flex items-center"
              data-testid="invest-now-btn"
            >
              {t('quantitative.investNow')}
              <ArrowRight className="ml-2" size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Fund: AAC Quantitative DAO - Bloque más espacioso */}
      <section className="relative" data-testid="quant-fund">
        <img 
          src="https://static.wixstatic.com/media/7a40f4_f757f5025cc04abf83177ff052676c58~mv2.jpg"
          alt="Blueprint Background"
          className="w-full h-[750px] md:h-[650px] object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="bg-gray-900/90 backdrop-blur-sm p-6 md:p-10 lg:p-16 max-w-6xl w-full text-white shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
              {/* Contenido izquierda - 3 columnas */}
              <div className="lg:col-span-3">
                <p className="text-sm tracking-[0.2em] text-cyan-400 mb-4">{t('quantitative.investmentThesis')}</p>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-5 lg:whitespace-nowrap">{t('quantitative.fundTitle')}</h2>
                <p className="text-lg text-gray-300 mb-5">{t('quantitative.fundSubtitle')}</p>
                <p className="text-gray-400 mb-5 text-sm leading-relaxed">
                  {t('quantitative.deckDesc')}
                </p>
                <p className="text-gray-400 mb-8 text-sm leading-relaxed">
                  {t('quantitative.layerDesc')}
                </p>
                
                {/* Imagen documento - SOLO MÓVIL */}
                <div className="lg:hidden mb-6 flex justify-center">
                  <a href="https://beacons.ai/accretionaxiomcapital" target="_blank" rel="noopener noreferrer" className="group">
                    <div className="relative bg-gray-800 p-2 rounded-lg shadow-xl border border-gray-600">
                      <div className="bg-gray-700 px-2 py-1 rounded-t flex items-center gap-1 mb-1">
                        <div className="w-2 h-2 rounded-full bg-red-400"></div>
                        <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-[8px] text-gray-400 ml-1">Deck.pdf</span>
                      </div>
                      <img 
                        src="https://static.wixstatic.com/media/7a40f4_ee889582a7c84970981afa258d79a0b7~mv2.jpg"
                        alt="Investment Deck"
                        className="w-64 h-auto rounded"
                      />
                    </div>
                  </a>
                </div>
                
                <div className="flex flex-row justify-center lg:justify-start gap-2 md:gap-4">
                  <a href="https://beacons.ai/accretionaxiomcapital" target="_blank" rel="noopener noreferrer" className="border-2 border-cyan-400 text-cyan-400 px-5 md:px-6 py-2.5 md:py-3 text-sm font-semibold hover:bg-cyan-400 hover:text-black transition-all inline-flex items-center">{t('quantitative.deck')}<ArrowRight className="ml-2" size={14} /></a>
                  <a href="#invest" className="bg-white text-black px-5 md:px-6 py-2.5 md:py-3 text-sm font-semibold hover:bg-gray-200 transition-all inline-flex items-center">{t('quantitative.invest')}<ArrowRight className="ml-2" size={14} /></a>
                </div>
              </div>
              {/* Separador vertical + documento - 2 columnas - DESKTOP */}
              <div className="hidden lg:flex lg:col-span-2 items-center justify-center gap-8">
                <div className="w-px bg-gray-600 self-stretch min-h-[300px]"></div>
                {/* Portada de documento estilizada */}
                <a href="https://beacons.ai/accretionaxiomcapital" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center text-center flex-1 group cursor-pointer">
                {/* Document-style frame */}
                <div className="relative group-hover:scale-[1.02] transition-transform">
                  {/* Paper shadow effect */}
                  <div className="absolute -inset-2 bg-gradient-to-br from-cyan-500/20 to-gray-700/20 rounded-lg blur-sm group-hover:from-cyan-500/30 transition-all"></div>
                  {/* Document container */}
                  <div className="relative bg-gray-800 p-3 rounded-lg shadow-xl border border-gray-600 group-hover:border-cyan-500/50 transition-colors">
                    {/* Document header bar */}
                    <div className="bg-gray-700 px-3 py-1.5 rounded-t flex items-center gap-1.5 mb-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                      <span className="text-[9px] text-gray-400 ml-2">AAC_Quantitative_Deck.pdf</span>
                    </div>
                    {/* Hero image as document cover */}
                    <img 
                      src="https://static.wixstatic.com/media/7a40f4_ee889582a7c84970981afa258d79a0b7~mv2.jpg"
                      alt="Investment Deck Cover"
                      className="w-64 h-auto rounded shadow-inner"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4 italic group-hover:text-cyan-400 transition-colors">{t('quantitative.fullDeck')}</p>
              </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Systematic Execution - Imágenes rectangulares grandes con modal profesional */}
      <section className="py-24 bg-gray-950" data-testid="quant-execution">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] text-cyan-400 mb-3">{t('quantitative.systematicExecution')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('quantitative.noBlackBoxes')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto italic">
              {t('quantitative.executionDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div 
              className="relative group overflow-hidden bg-gray-950 rounded-lg cursor-pointer border border-gray-800"
              onClick={() => openModal('https://static.wixstatic.com/media/7a40f4_e5d19010ea5248e79a511a81dcffc449~mv2.jpg', 'Strategy Chart 1')}
              data-testid="execution-img-1"
            >
              <div className="bg-gray-950 p-1">
                <img 
                  src="https://static.wixstatic.com/media/7a40f4_e5d19010ea5248e79a511a81dcffc449~mv2.jpg"
                  alt="Strategy Chart 1"
                  className="w-full aspect-[16/9] object-contain transition-transform duration-500 group-hover:scale-105 rounded"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent pointer-events-none"></div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                <div className="bg-cyan-500/90 rounded-full p-3">
                  <ZoomIn className="w-6 h-6 text-black" />
                </div>
              </div>
              <p className="absolute bottom-3 left-0 right-0 text-center text-gray-500 text-xs">{t('quantitative.clickEnlarge')}</p>
            </div>
            <div 
              className="relative group overflow-hidden bg-gray-950 rounded-lg cursor-pointer border border-gray-800"
              onClick={() => openModal('https://static.wixstatic.com/media/7a40f4_f0e578f00feb4ce29518139db3c27f46~mv2.jpg', 'Strategy Chart 2')}
              data-testid="execution-img-2"
            >
              <div className="bg-gray-950 p-1">
                <img 
                  src="https://static.wixstatic.com/media/7a40f4_f0e578f00feb4ce29518139db3c27f46~mv2.jpg"
                  alt="Strategy Chart 2"
                  className="w-full aspect-[16/9] object-contain transition-transform duration-500 group-hover:scale-105 rounded"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent pointer-events-none"></div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                <div className="bg-cyan-500/90 rounded-full p-3">
                  <ZoomIn className="w-6 h-6 text-black" />
                </div>
              </div>
              <p className="absolute bottom-3 left-0 right-0 text-center text-gray-500 text-xs">{t('quantitative.clickEnlarge')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Verified Results */}
      <section id="results" className="py-24 bg-gray-900 scroll-mt-24" data-testid="quant-results">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] text-cyan-400 mb-3">{t('quantitative.verifiedResultsTitle')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('quantitative.proofTitle')}
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto mb-4">
              {t('quantitative.proofDesc')}
            </p>
            <p className="text-gray-500 text-sm italic">
              {t('quantitative.proofNote')}
            </p>
          </div>

          {/* Gold Box with Auto Carousel - All payouts together */}
          <PayoutCarousel openModal={openModal} />

          {/* Top Trader Rankings - Much Bigger with Professional Modal */}
          <div className="max-w-7xl mx-auto">
            <p className="text-center text-gray-400 text-sm mb-8">{t('quantitative.rankedLeaderboards')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div 
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 cursor-pointer group hover:border-cyan-500/50 transition-all"
                onClick={() => openModal('https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/vlx3qn4e_Captura.PNG', 'FundingPips #1 Ranking')}
                data-testid="ranking-card-1"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-cyan-500 text-black text-lg font-bold px-4 py-2 rounded">#1</span>
                    <span className="text-white font-bold text-xl">{t('quantitative.topTrader')}</span>
                  </div>
                  <div className="bg-cyan-500/20 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/vlx3qn4e_Captura.PNG"
                    alt="FundingPips #1 Ranking"
                    className="w-full rounded-lg border border-gray-600 transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <div className="absolute top-4 left-4 bg-cyan-500 text-black text-sm font-bold px-3 py-1 rounded-full shadow-lg">
                    #1 RANK
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-4 text-center">FundingPips Leaderboard • Click to enlarge</p>
              </div>
              <div 
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 cursor-pointer group hover:border-cyan-500/50 transition-all"
                onClick={() => openModal('https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/d6cyampb_Captura%20%284%29.PNG', 'SwiftFunding #1 Ranking')}
                data-testid="ranking-card-2"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="bg-cyan-500 text-black text-lg font-bold px-4 py-2 rounded">#1</span>
                    <span className="text-white font-bold text-xl">{t('quantitative.topTrader')}</span>
                  </div>
                  <div className="bg-cyan-500/20 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/d6cyampb_Captura%20%284%29.PNG"
                    alt="SwiftFunding #1 Ranking"
                    className="w-full rounded-lg border border-gray-600 transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <div className="absolute top-4 left-4 bg-cyan-500 text-black text-sm font-bold px-3 py-1 rounded-full shadow-lg">
                    #1 RANK
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-4 text-center">SwiftFunding Leaderboard • Click to enlarge</p>
              </div>
            </div>
          </div>
          
          {/* Live DAO Performance CTA */}
          <div className="mt-16 text-center">
            <a 
              href="/quantitative/results" 
              className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black font-bold text-lg px-10 py-4 rounded-xl hover:from-cyan-400 hover:to-cyan-300 transition-all shadow-lg shadow-cyan-500/30"
              data-testid="live-dao-btn"
            >
              <BarChart3 size={22} />
              {t('quantitative.liveDao')}
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Float Animation for globe */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>

      {/* How It Works */}
      <section className="py-24 bg-gray-950" data-testid="quant-how">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] text-cyan-400 mb-3">{t('quantitative.howItWorks')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('quantitative.pathTitle')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('quantitative.pathDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/30">
                <span className="text-cyan-400 text-2xl font-bold">1</span>
              </div>
              <h3 className="text-white font-bold mb-2">{t('quantitative.step1Title')}</h3>
              <p className="text-gray-400 text-sm">{t('quantitative.step1Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/30">
                <span className="text-cyan-400 text-2xl font-bold">2</span>
              </div>
              <h3 className="text-white font-bold mb-2">{t('quantitative.step2Title')}</h3>
              <p className="text-gray-400 text-sm">{t('quantitative.step2Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/30">
                <span className="text-cyan-400 text-2xl font-bold">3</span>
              </div>
              <h3 className="text-white font-bold mb-2">{t('quantitative.step3Title')}</h3>
              <p className="text-gray-400 text-sm">{t('quantitative.step3Desc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500/30">
                <span className="text-cyan-400 text-2xl font-bold">4</span>
              </div>
              <h3 className="text-white font-bold mb-2">{t('quantitative.step4Title')}</h3>
              <p className="text-gray-400 text-sm">{t('quantitative.step4Desc')}</p>
            </div>
          </div>

          <div className="text-center">
            <a 
              href="#invest" 
              className="bg-cyan-500 text-gray-950 px-10 py-4 text-lg font-semibold hover:bg-cyan-400 transition-all inline-flex items-center"
            >
              {t('quantitative.investNow')}
              <ArrowRight className="ml-2" size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ - Desplegable */}
      <section className="py-24 bg-gray-900" data-testid="quant-faq">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] text-cyan-400 mb-3">{t('quantitative.faq')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('quantitative.faqTitle')}
            </h2>
          </div>

          <div className="space-y-4">
            <details className="group bg-gray-800/50 border border-gray-700 rounded-lg">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                <h3 className="text-white font-bold">{t('quantitative.faq1Q')}</h3>
                <span className="text-cyan-400 group-open:rotate-180 transition-transform">
                  <ArrowDown size={20} />
                </span>
              </summary>
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm">{t('quantitative.faq1A')}</p>
              </div>
            </details>
            
            <details className="group bg-gray-800/50 border border-gray-700 rounded-lg">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                <h3 className="text-white font-bold">{t('quantitative.faq2Q')}</h3>
                <span className="text-cyan-400 group-open:rotate-180 transition-transform">
                  <ArrowDown size={20} />
                </span>
              </summary>
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm">{t('quantitative.faq2A')}</p>
              </div>
            </details>
            
            <details className="group bg-gray-800/50 border border-gray-700 rounded-lg">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                <h3 className="text-white font-bold">{t('quantitative.faq3Q')}</h3>
                <span className="text-cyan-400 group-open:rotate-180 transition-transform">
                  <ArrowDown size={20} />
                </span>
              </summary>
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm">{t('quantitative.faq3A')}</p>
              </div>
            </details>
            
            <details className="group bg-gray-800/50 border border-gray-700 rounded-lg">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                <h3 className="text-white font-bold">{t('quantitative.faq4Q')}</h3>
                <span className="text-cyan-400 group-open:rotate-180 transition-transform">
                  <ArrowDown size={20} />
                </span>
              </summary>
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm">{t('quantitative.faq4A')}</p>
              </div>
            </details>
            
            <details className="group bg-gray-800/50 border border-gray-700 rounded-lg">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                <h3 className="text-white font-bold">{t('quantitative.faq5Q')}</h3>
                <span className="text-cyan-400 group-open:rotate-180 transition-transform">
                  <ArrowDown size={20} />
                </span>
              </summary>
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm">{t('quantitative.faq5A')}</p>
              </div>
            </details>
            
            <details className="group bg-gray-800/50 border border-gray-700 rounded-lg">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                <h3 className="text-white font-bold">{t('quantitative.faq6Q')}</h3>
                <span className="text-cyan-400 group-open:rotate-180 transition-transform">
                  <ArrowDown size={20} />
                </span>
              </summary>
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm">{t('quantitative.faq6A')}</p>
              </div>
            </details>
            
            <details className="group bg-gray-800/50 border border-gray-700 rounded-lg">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                <h3 className="text-white font-bold">{t('quantitative.faq7Q')}</h3>
                <span className="text-cyan-400 group-open:rotate-180 transition-transform">
                  <ArrowDown size={20} />
                </span>
              </summary>
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm">{t('quantitative.faq7A')}</p>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Ready to Execute - Con esfera animada de fondo como Wix */}
      <section className="relative py-40 overflow-hidden" data-testid="quant-cta">
        {/* Background Image */}
        <img 
          src="https://static.wixstatic.com/media/c837a6_0f9a4576e86442e3b124952322acdd72~mv2.jpg"
          alt="City Skyline"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gray-950/80"></div>
        
        {/* Esfera animada azul/cyan de fondo */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img 
            src="https://static.wixstatic.com/media/c837a6_159e86d4444649fcada7bff77ae670adf000.png"
            alt="Globe"
            className="w-[500px] h-[500px] object-contain opacity-50"
            style={{animation: 'float 6s ease-in-out infinite', filter: 'hue-rotate(180deg) saturate(1.2)'}}
          />
        </div>
        
        {/* Contenido centrado */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('quantitative.readyTitle')}
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            {t('quantitative.readyDesc')}
          </p>
          <a 
            href="#" 
            className="bg-cyan-500 text-gray-950 px-12 py-5 text-xl font-bold hover:bg-cyan-400 transition-all inline-flex items-center"
            data-testid="final-invest-btn"
          >
            {t('quantitative.investNow')}
            <ArrowRight className="ml-3" size={24} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuantitativePage;
